import { type ReactElement, useCallback, useEffect, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { RadioCard, RadioGroup } from '@/lib/base/radio-buttons/radio-buttons';

export type DataVisibility = 'org' | 'private';

export interface VisibilityChoiceModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (visibility: DataVisibility) => void;
  /**
   * Display name of the integration (e.g. "Gmail", "Outlook", "Google Calendar").
   * Drives the modal title/copy.
   */
  integrationLabel: string;
  /** When true, shows a spinner on the confirm button and disables interaction. */
  isConfirming?: boolean;
}

const PRIVATE_VALUE = 'private' as const;
const ORG_VALUE = 'org' as const;

export function VisibilityChoiceModal({
  isOpen,
  onOpenChange,
  onConfirm,
  integrationLabel,
  isConfirming = false,
}: VisibilityChoiceModalProps): ReactElement {
  const [value, setValue] = useState<DataVisibility>(PRIVATE_VALUE);

  useEffect(() => {
    if (isOpen) {
      setValue(PRIVATE_VALUE);
    }
  }, [isOpen]);

  const handleChange = useCallback((v: string) => {
    setValue(v as DataVisibility);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(value);
  }, [onConfirm, value]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal className='max-w-md'>
        <Dialog
          instance='choose-data-visibility'
          title='Choose data visibility'
          description={`Synced ${integrationLabel} data can be shared with your team or kept private. You can change this later in Settings.`}
          footer={
            <div className='flex justify-end gap-3'>
              <Button color='secondary' onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                color='primary'
                onClick={handleConfirm}
                isDisabled={isConfirming}
              >
                {isConfirming ? 'Connecting…' : 'Continue'}
              </Button>
            </div>
          }
        >
          <RadioGroup
            value={value}
            onChange={handleChange}
            aria-label='Data visibility'
            className='pb-2'
          >
            <RadioCard value={PRIVATE_VALUE} showIndicator>
              <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
                Keep private (only you)
              </p>
              <p className='text-sm text-[var(--color-text-secondary)]'>
                Only you can see synced threads and meetings.
              </p>
            </RadioCard>
            <RadioCard value={ORG_VALUE} showIndicator>
              <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
                Share with your team
              </p>
              <p className='text-sm text-[var(--color-text-secondary)]'>
                Other org members can see synced email threads and meetings.
              </p>
            </RadioCard>
          </RadioGroup>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
